import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';

import { isAuthExpired, redirectToLogin } from '../interceptors/auth-expired.interceptor';
import { AuthService } from '../services/auth/authservice';
import { SubscriptionService } from '../services/subscriptions/subscription-service';

export const subscriptionRequiredGuard: CanActivateFn & CanMatchFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const subscriptionService = inject(SubscriptionService);

  const session = await auth.ensureSession();
  if (!session?.authenticated) {
    return router.createUrlTree(['/login']);
  }

  try {
    const status = await firstValueFrom(
      subscriptionService.getMySubscriptionStatus().pipe(timeout(8000)),
    );

    if (status?.active) {
      return true;
    }

    return router.createUrlTree(['/subscription/select'], {
      queryParams: subscriptionRedirectQuery(),
    });
  } catch (error) {
    console.error('[SubscriptionRequiredGuard] failed to verify subscription status', error);

    if (isAuthExpired(error)) {
      const session = await auth.ensureSession();
      if (!session?.authenticated) {
        redirectToLogin();
        return false;
      }
    }

    return router.createUrlTree(['/subscription/select'], {
      queryParams: subscriptionRedirectQuery(),
    });
  }
};

function subscriptionRedirectQuery(): Record<string, string> | undefined {
  const accountType = typeof window === 'undefined'
    ? null
    : new URL(window.location.href).searchParams.get('accountType');
  const normalized = String(accountType ?? '').trim().toUpperCase();
  if (normalized === 'LANDLORD' || normalized === 'PROPERTY_MANAGER') {
    return { accountType: normalized, mode: 'workspace' };
  }
  return undefined;
}
